import { useUnit } from 'effector-react';
import $$api from 'models/api';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { FormProvider, useForm } from 'react-hook-form';
import 'react-phone-input-2/lib/bootstrap.css';
import { z } from 'zod';

import HookFormControl from 'components/HookFormControl';
import CommunityTab from 'components/StyledTemplates/CommunityTab';

import AuthService from 'services/AuthService';

import useResources from 'hooks/useResources';

import { type User } from 'types/zod/user';

type ChangePasswordProps = {
    awsId?: User['aws_id'];
};

const FormZod = z.object({
    old_password: z.string().min(8),
    new_password: z.string().min(8),
    new_password_confirmation: z.string().min(8),
});

const FormWithIdZod = z.object({
    new_password: z.string().min(8),
    new_password_confirmation: z.string().min(8),
});

const ChangePassword = (props: ChangePasswordProps) => {
    const { awsId } = props;

    const { t } = useResources();
    const form = useForm();

    const loading = useUnit($$api.users.changePasswordFx.pending);

    const onSubmit = async (formData: unknown) => {
        if (awsId) {
            const data = FormWithIdZod.parse(formData);
            await $$api.users.changePasswordFx({ awsId, password: data.new_password });
        } else {
            const data = FormZod.parse(formData);
            await AuthService.changePassword(data.old_password, data.new_password);
        }
    };

    return (
        <CommunityTab title={t('changePassword.title')} extraBody="display: block !important;">
            <FormProvider {...form}>
                <Form onSubmit={form.handleSubmit(onSubmit)}>
                    {!awsId && (
                        <Form.Row>
                            <Form.Group as={Col} sm={6}>
                                <HookFormControl.Password
                                    name="old_password"
                                    label={t('changePassword.old_password.label')}
                                    rules={{
                                        required: t('changePassword.old_password.errors.required'),
                                        minLength: {
                                            value: 8,
                                            message: t('changePassword.old_password.errors.length'),
                                        },
                                    }}
                                />
                            </Form.Group>
                        </Form.Row>
                    )}
                    <Form.Row>
                        <Form.Group as={Col} sm={6}>
                            <HookFormControl.Password
                                name="new_password"
                                label={t('changePassword.new_password.label')}
                                withHint={true}
                                rules={{
                                    required: t('changePassword.new_password.errors.required'),
                                    minLength: {
                                        value: 8,
                                        message: t('changePassword.new_password.errors.length'),
                                    },
                                }}
                            />
                        </Form.Group>
                        <Form.Group as={Col} sm={6}>
                            <HookFormControl.Password
                                name="new_password_confirmation"
                                label={t('changePassword.new_password_confirmation.label')}
                                withHint={true}
                                rules={{
                                    required: t('changePassword.new_password_confirmation.errors.required'),
                                    minLength: {
                                        value: 8,
                                        message: t('changePassword.new_password_confirmation.errors.length'),
                                    },
                                    validate: {
                                        mismatch: (value: any) => {
                                            return (
                                                value === form.getValues()?.new_password ||
                                                t('changePassword.new_password_confirmation.errors.mismatch')
                                            );
                                        },
                                    },
                                }}
                            />
                        </Form.Group>
                    </Form.Row>
                    <div className="text-right mt-4">
                        <Button
                            data-qaid="change_button"
                            variant="primary"
                            type="submit"
                            disabled={loading}
                            className="px-5"
                        >
                            {t('changePassword.change')}{' '}
                            {loading && (
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            )}
                        </Button>
                    </div>
                </Form>
            </FormProvider>
        </CommunityTab>
    );
};

export default ChangePassword;
